import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { translate as translateHOC } from 'react-admin';
import { withStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Dialog, IconButton, Paper, Typography } from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import Close from '@material-ui/icons/Close';
import { getCurrencyDecimals, getCurrencySymbol } from '../../helpers/invoiceHelpers';
import RefundForm from './RefundForm';
import SubmissionReceipt from './SubmissionReceipt';
import DiscardRefundDialog from './DiscardRefundDialog';
import { CONTENT_MAX_WIDTH, keys } from './constants';

const styles = ({ spacing: { unit }, palette }) => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: palette.grey[50],
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: palette.background.paper,
    borderBottom: `1px solid ${palette.divider}`,
    padding: `${unit}px ${unit * 2}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backBtn: {
    fontWeight: 600,
    textTransform: 'none',
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
  },
  container: {
    maxWidth: CONTENT_MAX_WIDTH,
    margin: '0 auto',
    padding: unit * 3,
  },
  titleBlock: {
    marginBottom: unit * 3,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: unit * 6,
  },
  emptyCard: {
    padding: unit * 6,
    textAlign: 'center',
  },
  backToInvoices: {
    marginTop: unit * 3,
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

// Fullscreen refund-by-item dialog. Props-driven (open/fetch/submit owned by the parent
// list surface via dataProvider); the per-rate allocation is local to RefundForm. The POST
// is async (no body), so a successful submit shows a "submitted, pending" receipt built from
// the operator's own input. `variant` switches the title and, in the unit surface, whether a
// paymentIntentPspReference is sent (handled by the parent).
const RefundByItemDialog = ({
  classes,
  translate,
  locale,
  open,
  variant,
  invoiceId,
  invoiceLabel,
  entityName,
  pspReference,
  currency,
  items,
  refundableAmount,
  refundableByRate,
  loading,
  submitting,
  submitted,
  onClose,
  onSubmit,
}) => {
  const [dirty, setDirty] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const decimals = getCurrencyDecimals(currency);
  const currencySymbol = getCurrencySymbol(currency, locale);
  const invoiceTitle = [invoiceLabel, entityName].filter(Boolean).join(' - ');
  const titleKey = variant === 'unit' ? keys.titleInstant : keys.title;

  const doClose = () => {
    setDiscardOpen(false);
    setDirty(false);
    onClose();
  };
  const requestClose = () => (dirty && !submitted ? setDiscardOpen(true) : doClose());

  const hasItems = Array.isArray(items) && items.length > 0;

  const renderBody = () => {
    if (submitted) {
      return (
        <>
          <SubmissionReceipt
            translate={translate}
            submitted={submitted}
            entityName={entityName}
            pspReference={pspReference}
            locale={locale}
            currency={currency}
          />
          <div className={classes.backToInvoices}>
            <Button id="refundByItem-back-button" variant="contained" color="primary" onClick={doClose}>
              {translate(keys.backToInvoices)}
            </Button>
          </div>
        </>
      );
    }
    if (loading || items == null) {
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    }
    if (!hasItems) {
      // Nothing refundable on a PAID invoice almost always means a refund is already
      // pending (the BE returns refundableAmount 0 while it processes), so hint that
      // rather than the generic "nothing to refund" message.
      const emptyBodyKey = refundableAmount > 0 ? keys.emptyBody : keys.emptyRefundInProgress;
      return (
        <Paper className={classes.emptyCard}>
          <Typography variant="subheading" gutterBottom>
            {translate(keys.emptyTitle)}
          </Typography>
          <Typography color="textSecondary" paragraph>
            {translate(emptyBodyKey)}
          </Typography>
          <Button variant="outlined" onClick={doClose}>
            {translate(keys.close)}
          </Button>
        </Paper>
      );
    }
    return (
      <RefundForm
        key={invoiceId}
        translate={translate}
        items={items}
        locale={locale}
        currency={currency}
        currencySymbol={currencySymbol}
        decimals={decimals}
        refundableAmount={refundableAmount}
        refundableByRate={refundableByRate}
        invoiceLabel={invoiceLabel}
        isSubmitting={submitting}
        onSubmit={onSubmit}
        onRequestClose={requestClose}
        onDirtyChange={setDirty}
      />
    );
  };

  return (
    <Dialog fullScreen open={open} onClose={requestClose}>
      <div className={classes.page}>
        <div className={classes.header}>
          <Button id="refundByItem-back-link" className={classes.backBtn} onClick={requestClose}>
            <ChevronLeft />
            {translate(keys.breadcrumb)}
          </Button>
          <IconButton id="refundByItem-close-button" onClick={requestClose} aria-label={translate(keys.close)}>
            <Close />
          </IconButton>
        </div>

        <div className={classes.scroll}>
          <div className={classes.container}>
            {!submitted && (
              <div className={classes.titleBlock}>
                <Typography variant="headline">{translate(titleKey, { invoice: invoiceTitle })}</Typography>
                {pspReference && (
                  <Typography variant="body2" color="textSecondary">
                    {`${translate(keys.pspReference)}: ${pspReference}`}
                  </Typography>
                )}
              </div>
            )}
            {renderBody()}
          </div>
        </div>
      </div>

      <DiscardRefundDialog
        open={discardOpen}
        translate={translate}
        onConfirm={doClose}
        onCancel={() => setDiscardOpen(false)}
      />
    </Dialog>
  );
};

RefundByItemDialog.propTypes = {
  classes: PropTypes.shape({}),
  translate: PropTypes.func.isRequired,
  locale: PropTypes.string,
  open: PropTypes.bool,
  variant: PropTypes.oneOf(['cumulative', 'unit']),
  invoiceId: PropTypes.string,
  invoiceLabel: PropTypes.string,
  entityName: PropTypes.string,
  pspReference: PropTypes.string,
  currency: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  refundableAmount: PropTypes.number,
  refundableByRate: PropTypes.arrayOf(PropTypes.shape({})),
  loading: PropTypes.bool,
  submitting: PropTypes.bool,
  submitted: PropTypes.shape({}),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

RefundByItemDialog.defaultProps = {
  variant: 'cumulative',
  locale: 'en_US',
};

const mapStateToProps = state => ({
  locale: state.i18n.locale,
});

export const RefundByItemDialogTest = RefundByItemDialog;

export default compose(
  connect(mapStateToProps),
  withStyles(styles),
  translateHOC
)(RefundByItemDialog);
